import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Select';
import { Loader2, Clock, TrendingUp, Settings, Info, HelpCircle } from 'lucide-react';
import pricingService from '../../services/pricingService';

const PricingConfigModal = ({ operator, open, onOpenChange, onSave }) => {
  const [formData, setFormData] = useState({
    pricingModel: 'FIXED_PLUS_VARIED',
    baseFare: 500,
    perKmRate: 15,
    freeDistance: 0,
    minimumFare: 500,
    driverCharges: 200,
    commissionRate: 0.10,
    commissionPaidBy: 'USER',
    taxRate: 0.18,
    surgeEnabled: true,
    surgeMultiplierPeak: 1.3,
    peakHoursMorningStart: '07:00',
    peakHoursMorningEnd: '09:00',
    peakHoursEveningStart: '18:00',
    peakHoursEveningEnd: '20:00',
  });
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch existing pricing config when modal opens
  useEffect(() => {
    if (operator && open) {
      fetchPricingConfig();
    }
  }, [operator, open]);

  const fetchPricingConfig = async () => {
    setFetchLoading(true);
    setError(null);
    try {
      const response = await pricingService.getTenantPricing(operator.id);

      if (response.success && response.data) {
        const config = response.data;

        setFormData({
          pricingModel: config.pricing_model || 'FIXED_PLUS_VARIED',
          baseFare: config.base_fare || 500,
          perKmRate: config.per_km_rate || 15,
          freeDistance: config.free_distance || 0,
          minimumFare: config.minimum_fare || 500,
          driverCharges: config.driver_charges || 200,
          commissionRate: config.commission_rate != null && config.commission_rate !== '' ? Number(config.commission_rate) : 0.10,
          commissionPaidBy: config.commission_paid_by || 'USER',
          taxRate: config.tax_rate != null && config.tax_rate !== '' ? Number(config.tax_rate) : 0.18,
          surgeEnabled: config.surge_enabled !== false,
          surgeMultiplierPeak: config.surge_multiplier_peak || 1.3,
          peakHoursMorningStart: config.peak_hours_morning_start ? config.peak_hours_morning_start.substring(0, 5) : '07:00',
          peakHoursMorningEnd: config.peak_hours_morning_end ? config.peak_hours_morning_end.substring(0, 5) : '09:00',
          peakHoursEveningStart: config.peak_hours_evening_start ? config.peak_hours_evening_start.substring(0, 5) : '18:00',
          peakHoursEveningEnd: config.peak_hours_evening_end ? config.peak_hours_evening_end.substring(0, 5) : '20:00',
        });
      }
    } catch (err) {
      console.error('Error fetching pricing config:', err);
      // If config doesn't exist, use defaults (already set in initial state)
      // Check multiple ways the 404 error might be structured
      const is404 = err.status === 404 ||
                    err.response?.status === 404 ||
                    err.message === 'Pricing configuration not found' ||
                    (err.success === false && err.message?.includes('not found'));

      if (is404) {
        setError('No pricing configuration found. Using default values.');
      }
    } finally {
      setFetchLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Convert form data to API format
      const apiData = {
        pricingModel: formData.pricingModel,
        baseFare: parseFloat(formData.baseFare),
        perKmRate: parseFloat(formData.perKmRate),
        freeDistance: parseFloat(formData.freeDistance),
        minimumFare: parseFloat(formData.minimumFare),
        driverCharges: parseFloat(formData.driverCharges),
        commissionRate: parseFloat(formData.commissionRate),
        commissionPaidBy: formData.commissionPaidBy,
        taxRate: parseFloat(formData.taxRate),
        surgeEnabled: formData.surgeEnabled,
        surgeMultiplierPeak: parseFloat(formData.surgeMultiplierPeak),
        peakHoursMorningStart: formData.peakHoursMorningStart + ':00',
        peakHoursMorningEnd: formData.peakHoursMorningEnd + ':00',
        peakHoursEveningStart: formData.peakHoursEveningStart + ':00',
        peakHoursEveningEnd: formData.peakHoursEveningEnd + ':00',
      };

      const response = await pricingService.updateTenantPricing(operator.id, apiData);

      if (response.success) {
        if (onSave) {
          await onSave(operator.id, apiData);
        }
        onOpenChange(false);
        alert('Pricing configuration updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update pricing');
      }
    } catch (err) {
      console.error('Error updating pricing:', err);
      setError(err.message || 'Failed to update pricing configuration');
    } finally {
      setLoading(false);
    }
  };

  if (!operator) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Configure Pricing - {operator.name}
          </DialogTitle>
          <DialogDescription>
            Set pricing configuration for this fleet operator
          </DialogDescription>
        </DialogHeader>

        {fetchLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin mr-2" />
            <span>Loading pricing configuration...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 p-6 pt-0">
            {error && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                {error}
              </div>
            )}

            {/* Pricing Model Selection with Explanation */}
            <div className="space-y-4 border-b pb-6">
              <div className="flex items-center gap-2">
                {/* DollarSign icon removed for consistent UI */}
                <h3 className="font-semibold text-lg">Step 1: Choose Pricing Model</h3>
              </div>
              <p className="text-sm text-gray-600">
                Select how you want to calculate fares for this operator's ambulances
              </p>

              <div className="space-y-3">
                <Select
                  value={formData.pricingModel}
                  onValueChange={(value) => setFormData({ ...formData, pricingModel: value })}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FIXED_PLUS_VARIED">
                      <div className="py-1">
                        <div className="font-medium">Fixed + Variable (Recommended)</div>
                        <div className="text-xs text-gray-500">Base fare + per km charge for extra distance</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="FIXED">
                      <div className="py-1">
                        <div className="font-medium">Fixed Price</div>
                        <div className="text-xs text-gray-500">Same price regardless of distance</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="VARIABLE">
                      <div className="py-1">
                        <div className="font-medium">Variable (Per KM)</div>
                        <div className="text-xs text-gray-500">Price calculated by distance only</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="FLAT">
                      <div className="py-1">
                        <div className="font-medium">Flat Routes</div>
                        <div className="text-xs text-gray-500">Fixed price for specific pre-configured routes</div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>

              </div>
            </div>

            {/* Base Pricing */}
            <div className="space-y-4 border-b pb-6">
              <div className="flex items-center gap-2">
                {/* DollarSign icon removed for consistent UI */}
                <h3 className="font-semibold text-lg">Step 2: Set Base Pricing</h3>
              </div>
              <p className="text-sm text-gray-600">
                Configure the base charges for ambulance service
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Base Fare - Always shown */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Base Fare (₹)
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step="1"
                    min="100"
                    max="10000"
                    value={formData.baseFare}
                    onChange={(e) => setFormData({ ...formData, baseFare: e.target.value })}
                    required
                    className="text-base"
                  />
                  <p className="text-xs text-gray-500">
                    {formData.pricingModel === 'FIXED' && 'Fixed charge for any distance'}
                    {formData.pricingModel === 'FIXED_PLUS_VARIED' && 'Starting fare for the trip'}
                    {formData.pricingModel === 'VARIABLE' && 'Base charge added to distance cost'}
                    {formData.pricingModel === 'FLAT' && 'Not used (routes have individual prices)'}
                  </p>
                </div>

                {/* Per Km Rate - Show for VARIABLE and FIXED_PLUS_VARIED */}
                {(formData.pricingModel === 'VARIABLE' || formData.pricingModel === 'FIXED_PLUS_VARIED') && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      Per Km Rate (₹)
                      <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="1"
                      min="5"
                      max="100"
                      value={formData.perKmRate}
                      onChange={(e) => setFormData({ ...formData, perKmRate: e.target.value })}
                      required
                      className="text-base"
                    />
                    <p className="text-xs text-gray-500">
                      {formData.pricingModel === 'VARIABLE' && 'Charge per kilometer traveled'}
                      {formData.pricingModel === 'FIXED_PLUS_VARIED' && 'Charge per km after free distance'}
                    </p>
                  </div>
                )}

                {/* Free Distance - Only for FIXED_PLUS_VARIED */}
                {formData.pricingModel === 'FIXED_PLUS_VARIED' && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-1">
                      Free Distance (km)
                      <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="50"
                      value={formData.freeDistance}
                      onChange={(e) => setFormData({ ...formData, freeDistance: e.target.value })}
                      required
                      className="text-base"
                    />
                    <p className="text-xs text-gray-500">
                      Included distance in base fare (e.g., 10 km)
                    </p>
                  </div>
                )}

                {/* Minimum Fare */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Minimum Fare (₹)
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step="1"
                    min="100"
                    max="5000"
                    value={formData.minimumFare}
                    onChange={(e) => setFormData({ ...formData, minimumFare: e.target.value })}
                    required
                    className="text-base"
                  />
                  <p className="text-xs text-gray-500">
                    Minimum charge for any trip
                  </p>
                </div>

                {/* Driver Charges */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Driver Charges (₹)
                    <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    max="1000"
                    value={formData.driverCharges}
                    onChange={(e) => setFormData({ ...formData, driverCharges: e.target.value })}
                    required
                    className="text-base"
                  />
                  <p className="text-xs text-gray-500">
                    Fixed driver fee per trip
                  </p>
                </div>
              </div>
            </div>

            {/* Commission & Tax */}
            <div className="space-y-4 border-b pb-6">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-purple-600" />
                <h3 className="font-semibold text-lg">Step 3: Commission & Tax</h3>
              </div>
              <p className="text-sm text-gray-600">
                Platform commission and government taxes
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Commission Rate (%)
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="50"
                      value={Math.round(formData.commissionRate * 100)}
                      onChange={(e) => setFormData({ ...formData, commissionRate: parseFloat(e.target.value) / 100 })}
                      required
                      className="text-base"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Platform commission (typically 10-15%)
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Commission Paid By
                    <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={formData.commissionPaidBy}
                    onValueChange={(value) => setFormData({ ...formData, commissionPaidBy: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USER">
                        <div className="py-1">
                          <div className="font-medium">User Pays</div>
                          <div className="text-xs text-gray-500">Added to user's total bill</div>
                        </div>
                      </SelectItem>
                      <SelectItem value="TENANT">
                        <div className="py-1">
                          <div className="font-medium">Operator Pays</div>
                          <div className="text-xs text-gray-500">Deducted from operator's revenue</div>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {formData.commissionPaidBy === 'USER'
                      ? 'Commission added to final price'
                      : 'Commission deducted from operator earnings'}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-1">
                    Tax Rate (GST %)
                    <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="30"
                      value={Math.round(formData.taxRate * 100)}
                      onChange={(e) => setFormData({ ...formData, taxRate: parseFloat(e.target.value) / 100 })}
                      required
                      className="text-base"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    GST/Tax (typically 18% in India)
                  </p>
                </div>
              </div>

            </div>

            {/* Step 4: Surge Pricing */}
            <div className="space-y-4 border-b pb-6">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-orange-600" />
                <h3 className="font-semibold text-lg">Step 4: Surge Pricing (Optional)</h3>
              </div>
              <p className="text-sm text-gray-600">
                Apply dynamic pricing during peak hours when demand is high
              </p>

              <div className="space-y-4">
                {/* Enable Surge Toggle */}
                {/* <div className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <input
                    type="checkbox"
                    id="surgeEnabled"
                    checked={formData.surgeEnabled}
                    onChange={(e) => setFormData({ ...formData, surgeEnabled: e.target.checked })}
                    className="h-5 w-5 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                </div> */}

                {formData.surgeEnabled && (
                  <>
                    {/* Surge Multiplier */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-1">
                        Peak Hour Surge Multiplier
                        <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          step="0.1"
                          min="1.0"
                          max="3.0"
                          value={formData.surgeMultiplierPeak}
                          onChange={(e) => setFormData({ ...formData, surgeMultiplierPeak: e.target.value })}
                          required
                          className="text-base"
                        />
                        <span className="text-sm text-gray-500">×</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Multiplier applied to fare during peak hours (1.5 = 50% increase, 2.0 = 100% increase)
                      </p>

                    </div>

                    {/* Peak Hours Configuration */}
                    <div className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-orange-600" />
                        <h4 className="text-sm font-medium">Configure Peak Hours</h4>
                      </div>
                      <p className="text-xs text-gray-600">
                        Set when surge pricing should apply (typically morning and evening rush hours)
                      </p>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Morning Peak Hours */}
                        <div className="space-y-3 bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm font-medium text-gray-700">Morning Peak</p>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-600">Start Time</label>
                              <Input
                                type="time"
                                value={formData.peakHoursMorningStart}
                                onChange={(e) => setFormData({ ...formData, peakHoursMorningStart: e.target.value })}
                                required
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">End Time</label>
                              <Input
                                type="time"
                                value={formData.peakHoursMorningEnd}
                                onChange={(e) => setFormData({ ...formData, peakHoursMorningEnd: e.target.value })}
                                required
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            e.g., 07:00 - 09:00
                          </p>
                        </div>

                        {/* Evening Peak Hours */}
                        <div className="space-y-3 bg-white rounded-lg p-3 border border-gray-200">
                          <p className="text-sm font-medium text-gray-700">Evening Peak</p>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-gray-600">Start Time</label>
                              <Input
                                type="time"
                                value={formData.peakHoursEveningStart}
                                onChange={(e) => setFormData({ ...formData, peakHoursEveningStart: e.target.value })}
                                required
                                className="mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-600">End Time</label>
                              <Input
                                type="time"
                                value={formData.peakHoursEveningEnd}
                                onChange={(e) => setFormData({ ...formData, peakHoursEveningEnd: e.target.value })}
                                required
                                className="mt-1"
                              />
                            </div>
                          </div>
                          <p className="text-xs text-gray-500">
                            e.g., 18:00 - 20:00
                          </p>
                        </div>
                      </div>

                    </div>
                  </>
                )}

                {!formData.surgeEnabled && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                    <p>
                      Surge pricing is currently disabled. Enable it to charge higher fares during peak hours when demand is high.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  'Save Configuration'
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PricingConfigModal;
